import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SyncIcon from '@mui/icons-material/SyncOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { GridColDef } from '@mui/x-data-grid';

import { StatusChip } from '@/components/Cards/Chips/StatusChip';

import {
  getProductStatusBg,
  getProductStatusColor,
  getProductStatusLabel,
  getShippingModeBg,
  getShippingModeColor,
  getShippingModeLabel,
} from './constants';

// "10x11x12,5" → { height:10, width:11, length:12, weight:5 }
const parseDimensions = (raw: string) => {
  const m = raw.match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?),(\d+(?:\.\d+)?)$/);
  if (!m) return null;
  return { height: m[1], width: m[2], length: m[3], weight: m[4] };
};

interface ColumnProps {
  onSyncProduct: (productId: string) => void;
  syncingIds: Set<string>;
  onConvertToMe1?: (productId: string) => void;
  convertingMe1Ids?: Set<string>;
  isMe1AsTailorMadeEnabled?: boolean;
}

export const fulfillProductsColumns = (props: ColumnProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    width: 160,
  },
  {
    field: 'title',
    headerName: 'Producto',
    flex: 1,
    minWidth: 200,
    maxWidth: 400,
    renderCell: ({ row }) => (
      <Box display="flex" alignItems="center" gap={1} height="100%">
        {row.pictures?.[0]?.url && (
          <Box
            component="img"
            src={row.pictures[0].url}
            alt={row.title}
            sx={{ width: 32, height: 32, objectFit: 'contain', borderRadius: '4px' }}
          />
        )}
        <Typography variant="body2">{row.title}</Typography>
      </Box>
    ),
  },
  {
    field: 'status',
    headerName: 'Estado Mercado Libre',
    width: 160,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ value }) => {
      const chip = (
        <StatusChip
          status={getProductStatusLabel(value ?? '')}
          bg={getProductStatusBg(value ?? '')}
          color={getProductStatusColor(value ?? '')}
        />
      );
      return (
        <Tooltip
          followCursor
          title={value === 'under_review' ? 'MercadoLibre está revisando este producto' : ''}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            sx={{ pointerEvents: 'auto' }}
          >
            {chip}
          </Box>
        </Tooltip>
      );
    },
  },
  {
    field: 'shipping.mode',
    headerName: 'Modo de envío',
    width: 150,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (_value: unknown, row: { shipping?: { mode?: string } }) =>
      row.shipping?.mode ?? '',
    renderCell: ({ value }) => (
      <Box>
        <StatusChip
          status={getShippingModeLabel(value ?? '')}
          bg={getShippingModeBg(value ?? '')}
          color={getShippingModeColor(value ?? '')}
        />
      </Box>
    ),
  },
  {
    field: 'shipping.dimensions',
    headerName: 'Dimensiones',
    width: 170,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (_value: unknown, row: { shipping?: { dimensions?: string } }) =>
      row.shipping?.dimensions ?? '',
    renderCell: ({ value }) => {
      const dim = value ? parseDimensions(value) : null;
      if (!dim) {
        return (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography variant="caption" color="text.disabled">
              Sin dimensiones
            </Typography>
          </Box>
        );
      }
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          lineHeight={1.3}
        >
          <Typography variant="caption" fontWeight={600}>
            {dim.height} × {dim.width} × {dim.length} cm
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dim.weight} gr
          </Typography>
        </Box>
      );
    },
  },
  {
    field: 'isSynchronized',
    headerName: 'Sincronizado',
    width: 130,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ value }) => (
      <Box>
        <StatusChip
          status={value ? 'Sí' : 'No'}
          bg={value ? '#D6F2DF' : '#F8F9FA'}
          color={value ? '#27AE60' : '#95A5A6'}
        />
      </Box>
    ),
  },
  {
    field: 'actions',
    headerName: 'Acciones',
    width: 120,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ row }) => {
      const isActive = row.status === 'active';
      const isMe2 = row.shipping?.mode === 'me2';
      const canSync = isActive && !isMe2;
      const canConvertToMe1 =
        Boolean(props.isMe1AsTailorMadeEnabled) &&
        row.shipping?.mode !== 'me1' &&
        row.shipping?.mode !== 'me2';
      const isConvertingMe1 = props.convertingMe1Ids?.has(row.id) ?? false;

      const syncTitle = !isActive
        ? 'Solo se pueden sincronizar productos activos'
        : isMe2
          ? 'Productos con envío ME2 no son sincronizables'
          : row.isSynchronized
            ? 'Actualizar producto del inventario Fulfill99'
            : 'Crear producto en inventario Fulfill99';

      const me1Title = !isActive
        ? 'Solo se pueden convertir productos activos'
        : isConvertingMe1
          ? 'Convirtiendo a ME1…'
          : 'Convertir a ME1';

      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={0.5}
          height="100%"
          sx={{ pointerEvents: 'auto' }}
        >
          <Tooltip followCursor title={syncTitle}>
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={!canSync || props.syncingIds.has(row.id)}
                onClick={() => props.onSyncProduct(row.id)}
              >
                {row.isSynchronized ? (
                  <SyncIcon fontSize="small" />
                ) : (
                  <AddCircleOutlineOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>

          {canConvertToMe1 && props.onConvertToMe1 && (
            <Tooltip followCursor title={me1Title}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={!isActive || isConvertingMe1}
                  onClick={() => props.onConvertToMe1?.(row.id)}
                >
                  <LocalShippingOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      );
    },
  },
];
